import { HTTP_TYPES } from '@/lib/enums';
import api from '@/api/api';
import { PLATFORM_HEADERS } from './platform';

const BASE = import.meta.env.VITE_125_PLATFORM_PUBLIC_BASE_URL;

// The identity module: integrations are Entra app registrations (Graph), configs are
// the Cosmos documents saying what each may receive. `appId` and `clientId` are the
// same Entra client id. Neither list pages.
const call = (method: string, path: string, body?: unknown) => {
	return api(method, `${BASE}${path}`, body, undefined, 'application/json', 'v1', {
		...PLATFORM_HEADERS,
	});
};

const get = (path: string) => {
	return call(HTTP_TYPES.GET, path);
};

const appPath = (appId: string) => {
	return `/integrations/${encodeURIComponent(appId)}`;
};

const configPath = (clientId: string) => {
	return `/integration-configs/${encodeURIComponent(clientId)}`;
};

// `from_template` omitted means a closed config. A template never carries a company,
// so `company_id` is always supplied here.
export interface CreateIntegrationBody {
	display_name: string;
	permissions?: string[];
	from_template?: string;
	company_id?: number | null;
	/** `client` is always added by the service. */
	tags?: string[];
}

// Every field optional and independent. `permissions` is the COMPLETE set: omitted roles
// are revoked, so leave the field out entirely to leave grants alone.
export interface UpdateIntegrationBody {
	display_name?: string;
	permissions?: string[];
	roll_secret?: boolean;
	// Only the named tags move; every other tag stays as it is.
	add_tags?: string[];
	remove_tags?: string[];
}

const response = {
	// Only integrations carrying every tag. None means every integration.
	async getIntegrations(tags: string[] = []): Promise<any> {
		const query = tags.length ? `?tags=${encodeURIComponent(tags.join(','))}` : '';
		return await get(`/integrations${query}`);
	},
	// Every tag in use with how many carry it, ordered by tag.
	async getTags(): Promise<any> {
		return await get('/integrations/tags');
	},
	async getIntegration(appId: string): Promise<any> {
		return await get(appPath(appId));
	},
	// The 201 carries `client_secret`: the only time it can ever be read.
	async createIntegration(body: CreateIntegrationBody): Promise<any> {
		return await call(HTTP_TYPES.POST, '/integrations', body);
	},
	// Carries `client_secret` only when `roll_secret` was true.
	async updateIntegration(appId: string, body: UpdateIntegrationBody): Promise<any> {
		return await call(HTTP_TYPES.PATCH, appPath(appId), body);
	},
	// Soft delete into the Entra recycle bin. The config goes too; `config_deleted` says if it did.
	async deleteIntegration(appId: string): Promise<any> {
		return await call(HTTP_TYPES.DELETE, appPath(appId));
	},
	async getIntegrationConfigs(): Promise<any> {
		return await get('/integration-configs');
	},
	async getIntegrationConfig(clientId: string): Promise<any> {
		return await get(configPath(clientId));
	},
	// For a client with no config (409 if one exists). Unset fields take the closed defaults.
	async createIntegrationConfig(body: Record<string, unknown>): Promise<any> {
		return await call(HTTP_TYPES.POST, '/integration-configs', body);
	},
	// Only once the registration is gone: a 409 while Entra still finds it.
	async deleteIntegrationConfig(clientId: string): Promise<any> {
		return await call(HTTP_TYPES.DELETE, configPath(clientId));
	},
	async getTemplates(): Promise<any> {
		return await get('/integration-configs/templates');
	},
	// Upsert by `name`. Editing a template does not change configs already created from it.
	async saveTemplate(body: {
		name: string;
		description?: string;
		values: Record<string, unknown>;
	}): Promise<any> {
		return await call(HTTP_TYPES.POST, '/integration-configs/templates', body);
	},
	// Responds with the removed template. Configs already created from it are unchanged.
	async deleteTemplate(name: string): Promise<any> {
		return await call(
			HTTP_TYPES.DELETE,
			`/integration-configs/templates/${encodeURIComponent(name)}`,
		);
	},
	// Full replace: every field required, nothing carried over.
	async replaceIntegrationConfig(clientId: string, body: Record<string, unknown>): Promise<any> {
		return await call(HTTP_TYPES.PUT, configPath(clientId), body);
	},
};

export default response;
